import { ConnectButton } from "@/components/connect-button";
import { HelpButton } from "@/components/help-button";
import { ApplicationLink, Link } from "@/components/links";
import {
  TransactionDialog,
  transactionInner,
} from "@/components/transaction/transaction-dialog";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { makeMintTransactions } from "@/lib/clients";
import { getHighlightClasses } from "@/lib/get-tokens-highlights";
import { formatNumber, shorten, max } from "@/lib/utils";
import {
  DSContractState,
} from "@myth-finance/dualstake-ts-sdk";
import { useWallet } from "@txnlab/use-wallet";
import {
  ArrowRight,
  ArrowRightLeft,
  ArrowLeft as ChevronLeft,
  ArrowRight as ChevronRight,
  CircleAlert,
  Hammer,
  Sunset,
  UserRound,
} from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { SwapDialog } from "./swap";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { useAlgoAcctInfo, useBalance } from "@/hooks/use-algo-account";
import { clearCStateCache } from "@/hooks/use-cstate";
import { useHistoryEvents } from "@/hooks/use-history-events";
import { DSContractListingExtd } from "@/lib/types";

interface MintFormProps {
  listing: DSContractListingExtd;
  cState: DSContractState;
}

const minAlgoSpend = 1e6;

export function MintForm({ listing, cState }: MintFormProps) {
  const { rate, lstName, asaName, asaUnitName, asaId, asaDecimals } = listing;
  const { refetch: refetchHistoryEvents } = useHistoryEvents(cState.appId);
  const { activeAddress } = useWallet();

  const [amount, setAmount] = useState<number>(0);
  const [asaAmount, setAsaAmount] = useState<number | undefined>(0);
  const formattedAmount = formatNumber(amount, 6, 6);
  const formattedAsaAmount =
    asaAmount !== undefined
      ? formatNumber(asaAmount, asaDecimals, asaDecimals)
      : "-";

  const [formError, setFormError] = useState<string | React.ReactNode>("");
  const inputRef = useRef<HTMLInputElement>(null);

  const highlightClass = useMemo(
    () => getHighlightClasses(asaId, "text"),
    [asaId]
  );
  const bgClass = useMemo(() => getHighlightClasses(asaId, "bg"), [asaId]);

  const { data: accountInfo, refetch: refetchAlgoAcct } =
    useAlgoAcctInfo(activeAddress);
  const userAlgoBalance = useBalance(1, accountInfo);
  const userAsaBalance = useBalance(asaId, accountInfo);

  const isInvalidAmount = asaAmount === undefined;

  const maxAlgoSpend = max(0n, cState.maxBalance - listing.algoBalance);

  const isAlgoOverStake = amount > maxAlgoSpend;
  const isAlgoUnderStake = amount && amount < minAlgoSpend;
  const isAlgoOverspend = amount ? userAlgoBalance < amount : false;
  const isAsaOverspend =
    asaAmount === undefined || asaAmount === 0
      ? false
      : userAsaBalance < asaAmount;

  const enableActionButton =
    asaAmount !== undefined &&
    !isAlgoOverspend &&
    !isAlgoUnderStake &&
    !isAlgoOverStake &&
    amount >= 1;

  const amountPreviewClass = enableActionButton
    ? `${highlightClass} font-bold`
    : "text-muted-foreground";

  const changeAmount = ({
    currentTarget: { value: valueStr },
  }: React.ChangeEvent<HTMLInputElement>) => {
    const value = Math.round(Number(valueStr) * 1e6);
    if (value < 0) {
      setAmount(0);
      setAsaAmount(undefined);
      return;
    }
    if (valueStr == "" || value === 0) {
      setAmount(0);
      setAsaAmount(0);
      return;
    }
    let num = (Number(rate) * value) / Number(cState.ratePrecision);
    if (rate > 0n)
      num += 1
    if (isNaN(num)) {
      setAmount(0);
      setAsaAmount(undefined);
    } else {
      setAmount(value);
      setAsaAmount(Math.ceil(num));
    }
  };

  console.log({ amount, asaAmount})

  useEffect(() => {
    if (isAlgoOverspend) {
      setFormError(`Not enough ALGO`);
    } else if (isAlgoOverStake) {
      setFormError(
        <div className="flex flex-col">
          <span className="font-bold">Over staking limit</span>
          <span>Max stakeable: {formatNumber(maxAlgoSpend)} ALGO</span>
        </div>
      );
    } else if (isAlgoUnderStake) {
      setFormError(
        <div className="flex flex-col">
          <span className="font-bold">Under minimum stake.</span>
          <span>Stake at least 1 ALGO</span>
        </div>
      );
    } else if (isInvalidAmount) {
      setFormError("Amount is invalid");
    } else {
      setFormError("");
    }
  }, [isAlgoOverspend, isAsaOverspend, isInvalidAmount, amount, asaAmount]);

  const missingAsaAmount =
    asaAmount === undefined ? 0 : asaAmount - userAsaBalance;
  const needsAsaOptin = missingAsaAmount ? userAsaBalance === -1 : false;

  const onSuccess = () => {
    setAmount(0);
    setAsaAmount(0);
    inputRef.current!.value = "";
    refetchAlgoAcct();
    clearCStateCache(cState.appId);
    refetchHistoryEvents();
  };

  return (
    <Card glowing="primary" className="w-[350px]">
      <CardHeader className="pb-5">
        <CardTitle className="font-normal -mx-5 text-center">
          <span className="whitespace-nowrap">
            Stake <span className="text-md">ALGO+{asaUnitName}</span>
          </span>
          <wbr />
          <span className="whitespace-nowrap">
            <ArrowRight size={16} className="inline -mt-1 mx-1" />
            <span className={`font-bold text-md ${highlightClass}`}>
              {lstName}
            </span>
          </span>
        </CardTitle>
        <CardDescription className="flex flex-col">
          <span className="self-end text-muted-foreground/55">
            Lost?{" "}
            <Link to="https://docs.myth.finance/dualSTAKE/mint">Read me</Link>
          </span>
        </CardDescription>
      </CardHeader>
      <CardContent className="-mt-1">
        <div className="grid w-full items-center gap-1">
          <div className="flex flex-col space-y-1.5">
            {maxAlgoSpend < minAlgoSpend ? (
              <Alert className="border-orange-500 mb-2">
                {listing?.sunset ? (
                  <Sunset className="stroke-orange-500" />
                ) : (
                  <CircleAlert className="stroke-orange-500" />
                )}
                <AlertTitle className="font-bold ml-2 text-orange-500">
                  Mint unavailable
                </AlertTitle>
                <AlertDescription className="ml-2 text-sm">
                  {listing.lstName} {" "}
                  {listing?.sunset ? (
                    <>
                      <Link to="https://docs.myth.finance/dualSTAKE/sunsetting">
                        has been sunset
                      </Link>
                      . Redeem all {listing.lstName} now.
                    </>
                  ) : (
                    "is at stake capacity"
                  )}
                </AlertDescription>
              </Alert>
            ) : null}
            <Label
              className="flex justify-between items-center pt-1"
              htmlFor="mint_algo_amount"
            >
              <div className="flex items-center gap-2 text-muted-foreground">
                Amount
              </div>
              {activeAddress ? (
                <div className="flex items-center gap-1 text-muted-foreground">
                  Available: {formatNumber(userAlgoBalance, 6, 2)}
                </div>
              ) : null}
            </Label>

            <div className="relative">
              <TooltipProvider>
                <Tooltip open={!!formError}>
                  <TooltipTrigger asChild>
                    <Input
                      id="mint_algo_amount"
                      autoComplete="off"
                      className={
                        isInvalidAmount || isAlgoOverspend
                          ? "ring-1 ring-destructive"
                          : ""
                      }
                      disabled={listing?.sunset}
                      onChange={changeAmount}
                      ref={inputRef}
                    />
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className={`bg-destructive`}>
                    {formError}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <div className="absolute top-[50%] right-2 -translate-y-[50%] text-sm text-muted-foreground pointer-events-none">
                ALGO
              </div>
            </div>
          </div>
          <span className="text-muted-foreground">+</span>
          <div className="flex flex-col space-y-1">
            <TooltipProvider>
              <Tooltip
                delayDuration={0}
                open={asaAmount === 0 ? undefined : false}
              >
                <TooltipTrigger asChild>
                  <div className="relative">
                    <Input
                      id="mint_ora_amount"
                      // placeholder={`${asaUnitName} amount`}
                      disabled
                      className={
                        isAsaOverspend ? "ring-1 ring-destructive" : ""
                      }
                      value={
                        asaAmount === 0
                          ? amount !== 0
                            ? "0"
                            : ""
                          : formattedAsaAmount
                      }
                      onChange={changeAmount}
                    />
                    <div className="absolute top-[50%] right-2 -translate-y-[50%] text-sm text-muted-foreground pointer-events-none">
                      {asaUnitName}
                    </div>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom" className={`bg-primary`}>
                  {asaUnitName} amount is automatically calculated based on the
                  amount of ALGO.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <div className={`flex justify-between mt-3 items-center space-y-1.5`}>
            <div>
              <ArrowRight className="text-muted-foreground" size={16} />
            </div>
            <span className={amountPreviewClass}>
              {enableActionButton || isAlgoOverspend ? formattedAmount : ""}{" "}
              {lstName}
            </span>{" "}
            <div className="invisible">
              <ArrowRight size={16} />
            </div>
          </div>
        </div>
      </CardContent>
      <CardFooter className="flex flex-col items-center">
        {isAsaOverspend && !formError ? (
          <div className="self-stretch flex bg-card border p-4 pt-3 border-destructive rounded-lg flex-col gap-1 mb-6">
            {/* <div className="mt-2 bg-card border p-4 self-center rounded-lg flex flex-col gap-2 w-full lg:w-96"> */}
            <div className="flex items-center justify-between">
              <div className="text-destructive font-bold">
                Not enough {listing.asaUnitName}
              </div>
              <HelpButton
                title={`Why do I need ${asaUnitName} to mint ${lstName}?`}
              >
                <div className="text-muted-foreground">
                  {asaName} ({asaUnitName}) rewards accrue into {lstName} via
                  the {asaUnitName}:{lstName} exchange rate.
                </div>
                <div>
                  You need to provide enough {asaUnitName} to account for the
                  accumulated rewards.
                </div>
                <div className="text-muted-foreground">
                  In some cases, an ASA community may decide to launch their
                  dualSTAKE token with an initial ASA requirement, such as when
                  they intend to provide generous farms.
                </div>
                <div className="text-muted-foreground">
                  Read more about minting{" "}
                  <Link to="https://docs.myth.finance/dualSTAKE/mint">
                    here
                  </Link>{" "}
                  and about the exchange rate{" "}
                  <Link to="https://docs.myth.finance/dualSTAKE/mint">
                    here
                  </Link>
                  .
                </div>
              </HelpButton>
            </div>

            <div className="text-sm  text-muted-foreground ">
              You need more {listing.asaUnitName} to perform this mint.
            </div>
            <div className="text-sm mb-2">
              You can purchase the required {listing.asaUnitName} amount, or try
              minting a lower amount of {listing.lstName}.
            </div>
            <SwapDialog
              tinymanEscrow={cState.lpId}
              tinymanAppId={Number(cState.tinymanAppId)}
              outAmount={missingAsaAmount}
              outAssetDecimals={listing.asaDecimals}
              outAssetId={Number(asaId)}
              outAssetUnitName={listing.asaUnitName}
              needsOptin={needsAsaOptin}
              trigger={(onClick) => (
                <Button
                  onClick={onClick}
                  className={`self-center ${bgClass} text-black/90 opacity-90 hover:opacity-100 hover:${bgClass}`}
                >
                  <ArrowRightLeft size={20} className="mr-1" /> Buy{" "}
                  {formatNumber(missingAsaAmount, asaDecimals, asaDecimals)
                    .length > 9
                    ? formatNumber(missingAsaAmount, asaDecimals, 2)
                    : formatNumber(
                        missingAsaAmount,
                        asaDecimals,
                        asaDecimals
                      )}{" "}
                  {asaUnitName}
                </Button>
              )}
            />
          </div>
        ) : null}
        <TransactionDialog
          type="mint"
          trigger={(onClick) => (
            <ConnectButton
              onClick={onClick}
              disabled={!enableActionButton || isAsaOverspend}
            >
              <Hammer size={20} className="mr-1" /> Mint {lstName}
            </ConnectButton>
          )}
          transactionBuilder={() =>
            makeMintTransactions(listing.appId, activeAddress!, amount)
          }
          title={
            <>
              <span className={highlightClass}>Mint {lstName}</span>
            </>
          }
          onSuccess={onSuccess}
        >
          {transactionInner(
            "mint",
            <>
              You are going to{" "}
              <strong>
                mint <span className={highlightClass}>{formattedAmount}</span>{" "}
                {lstName}
              </strong>
            </>,
            <>
              <div className="flex justify-between gap-2 mb-2">
                <div className="flex items-center">
                  <UserRound size={18} className="mr-1" /> Account
                </div>{" "}
                <span>{shorten(activeAddress!)}</span>
              </div>
              <div className="flex justify-between gap-2">
                <div className="flex items-center">
                  <ChevronRight size={18} className="mr-1" /> Depositing
                </div>
                <span>{formattedAmount} ALGO</span>
              </div>
              <div className="flex justify-between gap-2">
                <div className="flex items-center">
                  <ChevronRight size={18} className="mr-1" /> Depositing
                </div>
                <span>
                  {formattedAsaAmount} {asaUnitName}
                </span>
              </div>
              <div className="flex justify-between gap-2">
                <div className="flex items-center">
                  <ChevronRight size={18} className="mr-1" />
                  App call
                </div>
                <span className="text-right text-muted-foreground">
                  App <ApplicationLink id={listing.appId} />
                </span>
              </div>
              <div className="flex justify-between gap-2">
                <div className="flex items-center ml-3">
                  <ChevronLeft size={18} className="mr-1" /> Receiving
                </div>{" "}
                <span>
                  {formattedAmount} {lstName}
                </span>
              </div>
            </>
          )}
        </TransactionDialog>
      </CardFooter>
    </Card>
  );
}
